import { XIcon } from '@heroicons/react/solid';
import { Input, Modal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useRecoilState } from 'recoil';

import { isIphone } from '@/components/PdfReader/PdfNotes/SelectionTooltip';
import { useKeyboardHeight } from '@/components/PdfReader/PdfNotes/useKeyboardHeight';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { notify } from '@/components/shared/basic/notify';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';

import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { takeNoteModalAtom } from '@/atoms/pdfStates/takeNoteModalAtom';
import { createNote, getNoteById, updateNote } from '@/services/Notes';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const TakeNotePanel: FC<any> = ({
  open,
  onCancel,
  pdfUrl,
  book_id,
  pageNumber = 1,
  chapterNumber = 1,
  selectedText = '',
  takeNotePanelRef,
  chapterId,
  coordinates = null,
  isFullScreen = false,
  options = {},
}) => {
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(0);
  const [, setDrawerState] = useRecoilState(drawerPdfAtom);
  const [takeNoteModal, setTakeNoteModal] = useRecoilState(takeNoteModalAtom);
  const containerRef = useRef<HTMLDivElement>(null);
  const screens = useBreakpoint();
  const keyboardHeight = useKeyboardHeight();

  const handleCancel = () => {
    setNoteText('');
    onCancel && onCancel();
  };

  const handleSave = async () => {
    if (!book_id || !pageNumber || !selectedText || !noteText || !chapterId) {
      notify('Please fill in all required fields', 'warning');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        bookId: book_id,
        chapterId,
        pageNumber,
        excerpt: selectedText,
        content: noteText,
        chapterNumber: chapterNumber || 1,
        coordinates: coordinates
          ? {
              x: coordinates.x,
              y: coordinates.y,
              width: coordinates.width,
              height: coordinates.height,
            }
          : undefined,
      };
      if (takeNoteModal.noteId && takeNoteModal.value) {
        await updateNote(takeNoteModal.noteId, payload);
        setDrawerState({
          contentDrawer: false,
          notesDrawer: true,
        });
        notify('Note updated successfully', 'success');
      } else {
        await createNote(payload);
        notify('Note saved successfully', 'success');
      }
      setNoteText('');
      handleCancel();
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [open]);

  useEffect(() => {
    const fetchSingleNote = async () => {
      if (!takeNoteModal.noteId) return;
      try {
        const res = await getNoteById(takeNoteModal.noteId);
        setNoteText(res.content);
        setTakeNoteModal((prev) => ({
          ...prev,
          selectedText: res.excerpt,
          pageNumber: res.pageNumber,
        }));
      } catch {
        notify('Failed to fetch note data', 'error');
      }
    };
    if (takeNoteModal.noteId && open) fetchSingleNote();
  }, [takeNoteModal.noteId, open]);

  return (
    <Modal
      open={open}
      closable={false}
      footer={null}
      centered
      focusTriggerAfterClose={false}
      width='100vw'
      style={{ top: 0, padding: 0 }}
      rootClassName='!p-0 !m-0 !max-w-none !h-screen !flex !items-center !justify-center'
      destroyOnHidden
      classNames={{
        content: 'p-0  w-full bg-transparent',
        wrapper: 'p-0 m-0 w-full',
        body: 'p-0 m-0 w-full h-full',
        header: 'hidden',
        footer: 'hidden',
        mask: 'bg-[#000000]',
      }}
      {...(isFullScreen && { getContainer: false })}
      onCancel={handleCancel}
    >
      <div
        ref={takeNotePanelRef}
        className='flex flex-row h-[90vh] lg:h-[100vh] w-full lg:min-w-[1200px] bg-grayZl-950 max-w-[1600px] lg:mx-auto'
      >
        <ShouldRender check={screens.lg && open}>
          <div
            ref={containerRef}
            className='flex flex-col items-center justify-center rounded-l-xl w-[50%] xl:w-[40%] min-w-[520px] h-full shadow-2xl'
          >
            <Document file={pdfUrl} options={options} loading='Loading PDF...'>
              <Page
                pageNumber={pageNumber}
                width={width}
                loading='Loading...'
              />
            </Document>
          </div>
        </ShouldRender>

        <div className='flex flex-col w-full lg:w-[50%] xl:w-[60%] md:min-w-[420px] h-full rounded-r-xl'>
          <div className='flex flex-row items-center justify-between px-8 py-6 border-b border-[#232323]'>
            <span className='text-white text-lg font-semibold'>
              {takeNoteModal.noteId ? 'Edit' : 'Take'} a Note
            </span>
            <button
              onClick={handleCancel}
              className='bg-transparent border-none hover:opacity-80 p-0 ml-2'
            >
              <XIcon className='w-5 h-5 text-white' />
            </button>
          </div>
          <div className='px-8 py-4 bg-cartBgColor border-b border-[#232323] italic text-[#9f9ea6] text-sm min-h-[50px] leading-tight'>
            {selectedText
              ? `“${selectedText?.toLowerCase()}”`
              : 'No text selected.'}
          </div>
          <div
            className='p-4 flex-1 overflow-auto'
            style={{
              paddingBottom: screens.lg ? '0px' : `${keyboardHeight + 80}px`,
            }}
          >
            <Input.TextArea
              autoSize={{ minRows: 8, maxRows: isIphone() ? 8 : 10 }}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className='w-full resize-none text-[#c7c6cf] text-base leading-[1.5rem] border-none bg-transparent rounded-lg focus:border-[#ff5612] focus:shadow-none'
              placeholder='Enter note here'
            />
          </div>
          <div
            className='flex flex-row justify-end items-center border-t border-[#232323] px-8 py-3 md:py-6 gap-3 bg-[#151515]'
            style={{
              position: screens.lg ? 'static' : 'fixed',
              bottom: screens.lg ? 'auto' : keyboardHeight,
              left: 0,
              right: 0,
              zIndex: 50,
            }}
          >
            <ButtonDefault
              size={4}
              rounded={false}
              roundedSm={true}
              onClick={handleCancel}
              variant={ButtonVariants.UNSTYLED}
              className='border-[#282829] border'
            >
              <span className='flex text-sm gap-x-2 px-2 text-white bg-transparent whitespace-nowrap'>
                Cancel
              </span>
            </ButtonDefault>
            <ButtonDefault
              size={4}
              loading={loading}
              rounded={false}
              roundedSm={true}
              disabled={loading}
              onClick={handleSave}
              variant={ButtonVariants.PRIMARY}
            >
              <span className='flex text-sm gap-x-2 px-4 whitespace-nowrap'>
                Save
              </span>
            </ButtonDefault>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TakeNotePanel;
